import React, { useState, useRef, useCallback, forwardRef } from "react";
import {
  Button,
  Row,
  Col,
  NMForm,
  OutlinedButton,
  TextButton,
} from "@neumetric/blueprint-lib/components";
import { Collapse } from "antd";
import { useParams } from "react-router-dom";
import {
  getCurrentRisk,
  mitigatorActions,
  isRiskArchived,
} from "../../../store/mitigator";
import { useSelector, useDispatch } from "react-redux";

const ControlFormFields = [
  {
    label: "Control",
    attribute: "title",
    typeRepresentation: "TEXT",
    classes: { root: "mb-0 w-100 mr-16" },
  },
  // {
  //   label: "Priority",
  //   attribute: "priority",
  //   typeRepresentation: "DROPDOWN",
  //   classes: { root: "mb-0 w-25" },
  //   options: [
  //     { id: "Emergency", label: "Emergency" },
  //     { id: "High", label: "High" },
  //     { id: "Medium", label: "Medium" },
  //     { id: "Low", label: "Low" },
  //   ],
  // },
];

const RiskAcceptanceFormFields = [
  {
    label: "Justification",
    attribute: "justification",
    typeRepresentation: "HTML",
    required: false,
    classes: { root: "w-100" },
    tooltip: "Justification Field",
  },
  {
    label: "Impact",
    attribute: "risk_impact",
    typeRepresentation: "HTML",
    required: false,
    classes: { root: "w-100" },
    tooltip: "Impact Field",
  },
  {
    label: "Compensating Controls",
    attribute: "compensating_controls",
    typeRepresentation: "HTML",
    required: false,
    classes: { root: "w-100" },
    tooltip: "Compensating Controls Field",
  },
];

export const ControlsSection = forwardRef((props, ref) => {
  const { riskId } = useParams();
  const dispatch = useDispatch();
  const currentRisk = useSelector(getCurrentRisk);
  const isEditMode = Boolean(riskId && currentRisk);
  const isArchived = useSelector(isRiskArchived);

  const _ref = useRef(null);
  const [state, setState] = useState({
    showAddControl: false,
    controls: [],
  });

  React.useEffect(() => {
    if (riskId && currentRisk) {
      setState({ ...state, controls: currentRisk.risk_controls });
    }
  }, [riskId, currentRisk]);

  // handle add
  const handleAddControl = useCallback(() => {
    setState({ ...state, showAddControl: true });
  }, [state]);

  const handleSaveControl = useCallback(() => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    if (isEditMode) {
      const payload = {
        control: {
          title: values.title,
          priority: values.priority,
        },
        risk_id: currentRisk.id,
      };
      const action = mitigatorActions.createRiskControl(payload);
      action.onComplete = () => {
        handleCancelAddControl();
      };
      dispatch(action);
    } else {
      const { controls } = state;
      const newControls = [
        ...controls,
        { title: values.title, priority: values.priority },
      ];
      setState({ ...state, showAddControl: false, controls: newControls });
    }
  }, [state]);

  const handleCancelAddControl = () => {
    setState({ ...state, showAddControl: false });
  };
  const handleDeleteControl = useCallback(
    (control) => {
      const payload = {
        control_id: control.id,
        risk_id: riskId,
      };
      const action = mitigatorActions.deleteRiskControl(payload);
      action.onComplete = () => {
        handleCancelAddControl();
      };
      dispatch(action);
    },
    [state, riskId]
  );
  const validate = () => {
    return true;
  };
  const getValues = () => {
    return {
      risk_controls_attributes: state.controls,
    };
  };
  React.useImperativeHandle(ref, () => ({
    validate: validate,
    getValues: getValues,
  }));
  return (
    <Collapse
      className="w-100"
      defaultActiveKey={["controls"]}
      collapsible="disabled"
    >
      <Collapse.Panel
        key="controls"
        className=""
        showArrow={false}
        header={
          <span className="ff-exo2 fs-18 fw-600 line-28 text-filled">
            Controls
          </span>
        }
      >
        <Col className="w-100">
          {state.controls.length > 0 && (
            <ol className="w-100 pl-16 ff-exo2 fs-14">
              {state.controls.map((control, index) => (
                <li key={index} className="w-100">
                  <Row justify="between" align="center" className="w-100">
                    <span className="ff-exo2 fs-14 fw-400 line-20 text-filled">
                      {control.title}
                    </span>
                    {Boolean(riskId && currentRisk && !isArchived) && (
                      <TextButton
                        icon="DELETE"
                        classes={{ button: "ml-16" }}
                        onClick={() => handleDeleteControl(control)}
                      />
                    )}
                  </Row>
                </li>
              ))}
            </ol>
          )}
          {state.showAddControl && (
            <Row justify="between" align="center" className="w-100">
              <NMForm
                ref={_ref}
                className="flex-auto"
                fields={ControlFormFields}
                formData={{}}
                direction="horizontal"
              />
              <Button
                label="Save"
                icon="SAVE"
                classes={{ button: "ml-16" }}
                onClick={handleSaveControl}
              />
              <OutlinedButton
                label="Cancel"
                icon="CANCEL"
                classes={{ button: "ml-16" }}
                onClick={handleCancelAddControl}
              />
            </Row>
          )}
          {(isArchived ? false : !state.showAddControl) && (
            <Button label="Add Control" icon="ADD" onClick={handleAddControl} />
          )}
        </Col>
      </Collapse.Panel>
    </Collapse>
  );
});

export const RiskAcceptanceSection = forwardRef((_, ref) => {
  const dispatch = useDispatch();
  const _ref = useRef(null);
  const { riskId } = useParams();
  const currentRisk = useSelector(getCurrentRisk);
  const isArchived = useSelector(isRiskArchived);
  const [isEditMode, setIsEditMode] = useState(Boolean(!riskId));
  //added these helpers for displaying default values
  const [formKey, setFormKey] = useState(0);

  const DEFAULT_HELP = {
    justification:
      'Justification for Risk Acceptance is NOT necessary because decision for this Risk has been calculated to "Acceptable".<br/><br/>' +
      "Nevertheless, this field can be edited, if deemed necessary.",
    risk_impact:
      'Impact for Risk Acceptance is NOT necessary because decision for this Risk has been calculated to "Acceptable".<br/><br/>' +
      "Nevertheless, this field can be edited, if deemed necessary.",
    compensating_controls:
      'Compensating Controls for Risk Acceptance is NOT necessary because decision for this Risk has been calculated to "Acceptable".<br/><br/>' +
      "Nevertheless, this field can be edited, if deemed necessary.",
  };

  const isBlankHtml = (v) => {
    const s = (v ?? "").toString().trim();
    return !s || s === "<p></p>" || s === "<p><br></p>";
  };

  const withViewDefaults = (risk) => {
    const r = { ...(risk || {}) };
    if (isBlankHtml(r.justification))
      r.justification = DEFAULT_HELP.justification;
    if (isBlankHtml(r.risk_impact)) r.risk_impact = DEFAULT_HELP.risk_impact;
    if (isBlankHtml(r.compensating_controls))
      r.compensating_controls = DEFAULT_HELP.compensating_controls;
    return r;
  };

  const stripViewDefaultsForEdit = (risk) => {
    // when entering edit mode, show empty if the controller had no value
    const r = { ...(risk || {}) };
    if (
      isBlankHtml(r.justification) ||
      r.justification === DEFAULT_HELP.justification
    )
      r.justification = "";
    if (
      isBlankHtml(r.risk_impact) ||
      r.risk_impact === DEFAULT_HELP.risk_impact
    )
      r.risk_impact = "";
    if (
      isBlankHtml(r.compensating_controls) ||
      r.compensating_controls === DEFAULT_HELP.compensating_controls
    )
      r.compensating_controls = "";
    return r;
  };

  const stripDefaultsBeforeSave = (payload) => {
    const p = { ...(payload || {}) };
    if (p.justification === DEFAULT_HELP.justification) p.justification = "";
    if (p.risk_impact === DEFAULT_HELP.risk_impact) p.risk_impact = "";
    if (p.compensating_controls === DEFAULT_HELP.compensating_controls)
      p.compensating_controls = "";
    return p;
  };

  const validate = () => {
    return _ref.current.validate();
  };
  const getValues = () => {
    return {
      ..._ref.current.getValues(),
    };
  };
  // const formInitialValues = React.useMemo(() => {
  //   if (riskId && currentRisk) {
  //     return {
  //       ...currentRisk,
  //     };
  //   }
  //   return {};
  // }, [riskId, currentRisk]);

  const formInitialValues = React.useMemo(() => {
    if (riskId && currentRisk) {
      return isEditMode
        ? stripViewDefaultsForEdit(currentRisk)
        : withViewDefaults(currentRisk);
    }
    return {};
  }, [riskId, currentRisk, isEditMode]);

  React.useImperativeHandle(ref, () => ({
    validate: validate,
    getValues: getValues,
  }));

  const handleEdit = () => {
    setIsEditMode(true);
    setFormKey((k) => k + 1); //added this line to remove default value
  };

  const getButtonProps = () => {
    if (isEditMode) {
      return { label: "Save", icon: "SAVE", onClick: handleSave };
    }
    return { label: "Edit", icon: "EDIT", onClick: handleEdit };
  };

  const updateFormData = React.useCallback(() => {
    const values = {
      justification: currentRisk.justification,
      risk_impact: currentRisk.risk_impact,
      compensating_controls: currentRisk.compensating_controls,
    };
    _ref.current.updateFormData(values);
    setIsEditMode(false);
  }, [currentRisk]);

  React.useEffect(() => {
    if (riskId && currentRisk && isEditMode) {
      updateFormData();
    }
  }, [riskId, currentRisk]);

  // const handleSave = () => {
  //   if (!validate()) {
  //     return;
  //   }
  //   const values = getValues();
  //   const payload = { ...currentRisk, ...values };
  //   const action = mitigatorActions.updateRisk(payload);
  //   dispatch(action);
  // };
  const handleSave = () => {
    if (!validate()) return;

    const values = getValues();
    let payload = { ...currentRisk, ...values };
    payload = stripDefaultsBeforeSave(payload);

    const action = mitigatorActions.updateRisk(payload);
    action.onComplete = () => {
      setIsEditMode(false);
      setFormKey((k) => k + 1); // show view defaults again if still blank
    };
    dispatch(action);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setFormKey((k) => k + 1); //remount back to view defaults
  };

  return (
    <Collapse
      className="w-100 mt-16"
      defaultActiveKey={["risk"]}
      collapsible="disabled"
    >
      <Collapse.Panel
        key="risk"
        className=""
        showArrow={false}
        header={
          <Row align="center" justify="between" className="w-100">
            <span className="ff-exo2 fs-18 fw-600 line-28 text-filled">
              Risk Acceptance Information
            </span>
            {Boolean(riskId && currentRisk && !isArchived) && (
              <Row align="center">
                <Button {...getButtonProps()} />
                {isEditMode && (
                  <OutlinedButton
                    label="Cancel"
                    classes={{ button: "ml-16" }}
                    icon="CANCEL"
                    onClick={handleCancel}
                  />
                )}
              </Row>
            )}
          </Row>
        }
      >
        <Col className="w-100">
          <NMForm
            key={formKey} //added this to display default values
            ref={_ref}
            direction="vertical"
            className="flex-auto"
            fields={RiskAcceptanceFormFields}
            formData={formInitialValues}
            readOnly={!isEditMode}
          />
        </Col>
      </Collapse.Panel>
    </Collapse>
  );
});
